import { Box, Chip, IconButton, TextField, Button } from "@mui/material";
import { Modal, ModalFooter, ModalHeader } from "reactstrap";
import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { UsernameSelectField, styles } from "../../pages/MyTracks/styles";
import { EmailInviteTextField, useStyles } from "../../pages/MyTracks/styles";
import { InvitedCollaborator, InviteTemplate } from "../../proto/api_pb";
import { InvitedCollaboratorState } from "../../proto/api_pb";
import { Track, Collaborator, User, TrackFile } from "../../proto/api_pb";
import { emailRegex } from "../constants";
import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { AuthContext, UserDetailsContext } from "../../contexts";
import { UserDetailsContextInterface } from "../../contexts";
import { RpcError } from "grpc-web";
import { postTrackFile } from "../../services/trackService";
import { postTrack, sendInvites } from "../../services/trackService";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { collaboratorsModalStyles } from "./styles";
import { APP_BACKGROUND_COLOR, APP_COLOR, FONT_FAMILY } from "../../theme";
import { useNavigate } from "react-router-dom";

const CollabModal: React.FC<{
  openCollabsModal: boolean;
  handleCloseCollabsModal: () => void;
  trackToEdit: Track | TrackFile | undefined;
  handleFetchTrackFiles?: () => Promise<void>;
}> = ({
  openCollabsModal,
  handleCloseCollabsModal,
  trackToEdit,
  handleFetchTrackFiles,
}) => {
  const classes = useStyles();
  const [collabVal, setCollabVal] = useState("");
  const [emailInvite, setEmailInvite] = useState("");
  const [emailInviteList, setEmailInviteList] = useState<string[]>([]);
  const [usersList, setUsersList] = useState<Array<User>>([]);
  const { allUsers } = useContext(UserDetailsContext);
  const { userName } =
    useContext<UserDetailsContextInterface>(UserDetailsContext);
  const { userId } = useContext(AuthContext);
  const [collaborators, setCollaborators] = useState<Array<Collaborator>>([]);
  const { getValidAccessToken } = useGetValidAccessToken();
  const [addedCollabIds, setAddedCollabIds] = useState<string[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    let userL = allUsers.filter((user) => user.getUsername() !== userName);
    if (trackToEdit) {
      const fetchedCollabs = trackToEdit
        .getCollaboratorsList()
        .map((item) => item.getUsername());
      userL = userL.filter(
        (usr) => !fetchedCollabs.includes(usr.getUsername())
      );
      setCollaborators(trackToEdit.getCollaboratorsList());
    }
    setUsersList(userL);
  }, [allUsers, userName, trackToEdit]);

  const addCollaborator = (newCollaborator: string) => {
    if (newCollaborator) {
      if (
        collaborators.find(
          (collaborator) => collaborator.getUsername() === newCollaborator
        ) === undefined &&
        usersList.some((user) => user.getUsername() === newCollaborator)
      ) {
        const addedCollab = usersList.find(
          (user) => user.getUsername() === newCollaborator
        );

        if (addedCollab) {
          const newCollab = new Collaborator();
          newCollab.setUserId(addedCollab?.getUserId());
          newCollab.setUsername(addedCollab?.getUsername());
          const updatedCollaborators = [...collaborators, newCollab];

          setAddedCollabIds([...addedCollabIds, addedCollab.getUserId()]);
          setCollaborators(updatedCollaborators);
        }

        const newUserList = usersList.filter(
          (user) => user.getUsername() !== newCollaborator
        );
        setUsersList([...newUserList]);
        setCollabVal("");
      } else {
        toast.info("Username does not exist");
      }
      return true;
    }
  };

  const removeCollaborator = (collaborator: Collaborator) => {
    const updatedCollaborators = collaborators.filter(
      (item) => item !== collaborator
    );
    setCollaborators(updatedCollaborators);

    // updating userIds list that is being passed in sendInvite
    const updatedUserIdList = addedCollabIds.filter(
      (item) => item !== collaborator.getUserId()
    );
    setAddedCollabIds([...updatedUserIdList]);

    const newUser = new User();
    newUser.setUsername(collaborator.getUsername());
    newUser.setUserId(collaborator.getUserId());

    const newUsersList = [...usersList, newUser];
    setUsersList([...newUsersList]);
  };

  const handleCollabInvites = (event: React.SyntheticEvent<Element, Event>) => {
    if (emailInvite) {
      if (emailInvite.match(emailRegex)) {
        setEmailInviteList([...emailInviteList, emailInvite]);
        setEmailInvite("");
      } else {
        toast.info("Invalid email address!");
      }
    }
  };

  const handleRemoveEmail = (email: string) => {
    const updateEmailList = emailInviteList.filter((item) => item !== email);
    setEmailInviteList(updateEmailList);
  };

  const handleConfirmCollabs = async () => {
    const token = await getValidAccessToken();
    const track = trackToEdit;
    if (track) {
      track.setCollaboratorsList(collaborators);

      // adding the invited collabs to the track obj
      if (emailInviteList.length !== 0) {
        const invitedCollabList: InvitedCollaborator[] = [];

        // for each email (invitedCollab) creating the obj and
        // adding it to the list
        emailInviteList.forEach((email) => {
          const newInvitedCollab = new InvitedCollaborator();
          newInvitedCollab.setEmail(email);
          newInvitedCollab.setInvitedCollaboratorState(
            InvitedCollaboratorState.INVITED_COLLABORATOR_STATE_PENDING
          );
          invitedCollabList.push(newInvitedCollab);
        });
        track.setInvitedCollaboratorsList([
          ...trackToEdit.getInvitedCollaboratorsList(),
          ...invitedCollabList,
        ]);
      }
      // set attribute in track obj before api call
      if (track instanceof Track && trackToEdit instanceof Track) {
        const postTrackResponse = await postTrack(track, token);
        if (postTrackResponse instanceof RpcError) {
          setEmailInviteList([]);
          handleCloseCollabsModal();
          throw postTrackResponse;
        }
        // sending email invitations if new collab or email is added
        if (emailInviteList.length !== 0 || addedCollabIds.length !== 0) {
          try {
            await sendInvites(
              await getValidAccessToken(),
              emailInviteList,
              addedCollabIds,
              userId,
              `${window.location.origin}/track/${track.getId()}`,
              track.getTitle(),
              InviteTemplate.INVITE_TEMPLATE_COLLABORATE_TRACK
            );
          } catch (error) {
            console.log(error);
          }
        }
      }
      if (track instanceof TrackFile && trackToEdit instanceof TrackFile) {
        const postFileResponse = await postTrackFile(track, token);
        if (postFileResponse instanceof RpcError) {
          setEmailInviteList([]);
          handleCloseCollabsModal();
          throw postFileResponse;
        }
        // sending email invitations if new collab or email is added
        if (emailInviteList.length !== 0 || addedCollabIds.length !== 0) {
          try {
            await sendInvites(
              await getValidAccessToken(),
              emailInviteList,
              addedCollabIds,
              userId,
              `${window.location.origin}/my-files`,
              track.getFilename(),
              InviteTemplate.INVITE_TEMPLATE_COLLABORATE_FILE
            );
          } catch (error) {
            console.log(error);
          }
        }
      }
      setEmailInviteList([]);
      handleCloseCollabsModal();
      toast.success("Contributors updated");
    }
    // if we got to this modal from mytracks or myfiles
    if (handleFetchTrackFiles) {
      try {
        handleFetchTrackFiles();
      } catch (error) {
        console.log(error);
      }
    }
    // if we got here from track page
    else {
      navigate("/my-tracks");
    }
  };

  return (
    <Modal isOpen={openCollabsModal} toggle={handleCloseCollabsModal} centered>
      <ModalHeader style={{ backgroundColor: APP_BACKGROUND_COLOR }}>
        Edit Collaborators
        <IconButton
          onClick={handleCloseCollabsModal}
          style={styles.postMessageModalIcon}
        >
          <CloseIcon style={{ color: APP_COLOR }} />
        </IconButton>
      </ModalHeader>
      <ModalFooter style={collaboratorsModalStyles.modalFooter}>
        <Box sx={collaboratorsModalStyles.userNameLabelContainer}>
          <Box sx={{ width: "80%" }}>
            <label
              htmlFor="newCollaboratorUser"
              style={styles.modalCreatorUsername}
            >
              {"Collaborator's Username"}
            </label>
            <UsernameSelectField
              freeSolo
              forcePopupIcon={false}
              isOptionEqualToValue={(option, value) => option === value}
              inputValue={collabVal}
              onInputChange={(
                event: React.SyntheticEvent<Element, Event>,
                value: string
              ) => {
                setCollabVal(value);
              }}
              options={usersList.map((item) => item.getUsername()).sort()}
              renderInput={(params) => (
                <TextField
                  style={{
                    backgroundColor: APP_BACKGROUND_COLOR,
                    color: "white",
                  }}
                  {...params}
                  label=" "
                  InputLabelProps={{
                    shrink: false,
                  }}
                  classes={{ root: classes.customTextField }}
                />
              )}
              renderOption={(props, option: any) => (
                <li {...props} style={{ fontFamily: FONT_FAMILY }}>
                  {option}
                </li>
              )}
            />
          </Box>
          <Button
            disabled={collabVal === ""}
            sx={collaboratorsModalStyles.addCollaboratorButton}
            size="small"
            variant="contained"
            onClick={() => addCollaborator(collabVal)}
          >
            Add
          </Button>
        </Box>

        <Box style={{ marginBottom: 10, width: "100%" }}>
          <Box>
            {collaborators &&
              collaborators.length > 0 &&
              collaborators.map((collaborator, index) => (
                <Box
                  className="collaborator"
                  style={{ width: "100%" }}
                  key={index}
                >
                  <p style={{ fontFamily: FONT_FAMILY, flexGrow: 1 }}>
                    {collaborator.getUsername()}
                  </p>
                  {/* Owner */}
                  {collaborator.getUserId() === trackToEdit?.getOwnerId() && (
                    <Chip
                      label="Owner"
                      color="primary"
                      sx={{
                        marginRight:
                          trackToEdit?.getOwnerId() === userId ? 1 : 0,
                      }}
                    />
                  )}
                  {/* You */}
                  {collaborator.getUserId() === userId && (
                    <Chip label="You" color="info" />
                  )}
                  {/* Owner and user (you) cannot be removed */}
                  {collaborator.getUserId() !== trackToEdit?.getOwnerId() && (
                    <IconButton
                      onClick={() => removeCollaborator(collaborator)}
                    >
                      <DeleteForeverIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
          </Box>
        </Box>

        <div style={collaboratorsModalStyles.inviteCollaboratorMainContainer} />
        <Box sx={collaboratorsModalStyles.inviteCollaboratorContainer}>
          <Box sx={{ width: "80%" }}>
            <label htmlFor="emailInvite" style={styles.modalCreatorUsername}>
              {"Invite Collaborator"}
            </label>
            <EmailInviteTextField
              hiddenLabel
              variant="outlined"
              classes={{ root: classes.customTextField }}
              placeholder="Send an invite email to a collaborator"
              value={emailInvite}
              onChange={(
                event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
              ) => setEmailInvite(event.target.value)}
            />
          </Box>
          <Button
            disabled={emailInvite === ""}
            sx={collaboratorsModalStyles.addCollaboratorButton}
            size="small"
            variant="contained"
            onClick={handleCollabInvites}
          >
            Add
          </Button>
        </Box>

        <Box style={{ marginBottom: 10, width: "100%" }}>
          <Box>
            {emailInviteList &&
              emailInviteList.length > 0 &&
              emailInviteList.map((email, index) => (
                <Box
                  className="collaborator"
                  style={{ width: "100%" }}
                  key={index}
                >
                  <p style={{ fontFamily: FONT_FAMILY, flexGrow: 1 }}>
                    {email}
                  </p>

                  <IconButton onClick={() => handleRemoveEmail(email)}>
                    <DeleteForeverIcon />
                  </IconButton>
                </Box>
              ))}
          </Box>
          {/* if there are invited collabs with pending invitations */}

          <Box>
            {trackToEdit &&
              trackToEdit.getInvitedCollaboratorsList().length > 0 &&
              trackToEdit.getInvitedCollaboratorsList().map((item, index) => {
                if (
                  item.getInvitedCollaboratorState() ===
                  InvitedCollaboratorState.INVITED_COLLABORATOR_STATE_PENDING
                )
                  return (
                    <Box
                      className="collaborator"
                      style={{ width: "100%" }}
                      key={index}
                    >
                      <p style={{ fontFamily: FONT_FAMILY, flexGrow: 1 }}>
                        {item.getEmail()}
                      </p>
                      <Chip label="Sent" color="primary" />
                    </Box>
                  );
              })}
          </Box>
        </Box>

        <hr />
        <Button
          sx={collaboratorsModalStyles.confirmButton}
          size="small"
          variant="contained"
          onClick={handleConfirmCollabs}
        >
          Confirm
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CollabModal;
